import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { BookOpen, CheckCircle, Clock, ChevronRight, Flag, ArrowLeft } from 'lucide-react';
import { SUPPORTED_LANGUAGES, type LanguageCode, type CEFRLevel, CEFR_LEVELS } from '@/types/language';
import { SPANISH_A1_UNITS } from '@/data/spanish';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const UNIT_DATA: Record<string, typeof SPANISH_A1_UNITS> = {
  es: SPANISH_A1_UNITS,
};

const LessonsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeLang = (searchParams.get('lang') || 'es') as LanguageCode;
  const activeLevel = (searchParams.get('level') || 'A1') as CEFRLevel;
  const activeUnitId = searchParams.get('unit');

  useSeoMeta({
    title: `Lessons - LinguaNostr`,
    description: `Learn ${SUPPORTED_LANGUAGES.find(l => l.code === activeLang)?.name} with evidence-based lessons.`,
  });

  const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === activeLang)!;
  const units = UNIT_DATA[activeLang] || [];

  const currentUnit = activeUnitId ? units.find((u) => u.id === activeUnitId) : null;

  // Language + Level selector
  const selectLang = (code: LanguageCode) => setSearchParams({ lang: code, level: 'A1' });
  const selectLevel = (level: CEFRLevel) => setSearchParams({ lang: activeLang, level });

  if (currentUnit) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setSearchParams({ lang: activeLang, level: activeLevel })}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to units
        </Button>

        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{currentUnit.title}</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">{currentUnit.description}</p>
        </div>

        <div className="space-y-3">
          {currentUnit.subUnits.map((sub) => {
            const completedPhases = sub.phases.filter((p) => sub.activities[p]?.length > 0);
            return (
              <Card key={sub.id} className="group cursor-pointer border-slate-200 transition-all hover:border-blue-300 dark:border-slate-800 dark:hover:border-blue-700">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg text-slate-900 dark:text-white">
                        Sub-Unit {sub.order}: {sub.title}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {sub.estimatedMinutes} min | {completedPhases.length} phases
                      </CardDescription>
                    </div>
                    <div className="flex gap-1">
                      {sub.phases.map((p) => (
                        <Badge key={p} variant="outline" className="text-xs capitalize">
                          {p === 'receptive_processing' ? 'R' :
                           p === 'structured_production' ? 'S' :
                           p === 'awareness_raising' ? 'A' :
                           p === 'retrieval_recycling' ? 'RR' :
                           p === 'autonomy_assessment' ? 'AA' :
                           p.charAt(0).toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {sub.phases.map((phase) => {
                      const actCount = sub.activities[phase]?.length || 0;
                      return (
                        <div key={phase} className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          {actCount} {phase.replace('_', ' ')}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lessons</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Structured curriculum following Conti's EPI methodology
        </p>
      </div>

      {/* Language selector */}
      <div className="flex flex-wrap gap-2">
        {SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en').map((lang) => (
          <Button
            key={lang.code}
            variant={activeLang === lang.code ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectLang(lang.code)}
            className="gap-2"
          >
            <span>{lang.flag}</span>
            {lang.name}
          </Button>
        ))}
      </div>

      {/* Level selector */}
      <div className="flex flex-wrap gap-2">
        {langInfo.levelsAvailable.map((level) => (
          <Button
            key={level}
            variant={activeLevel === level ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectLevel(level)}
          >
            {level}
          </Button>
        ))}
      </div>

      {/* Units */}
      {units.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BookOpen className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="text-slate-500">
              Curriculum for {langInfo.name} {activeLevel} is being developed.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {units.map((unit) => {
            const progress = unit.subUnits.filter((s) => s.phases.length > 0).length;
            const total = unit.subUnits.length;
            return (
              <Card
                key={unit.id}
                onClick={() => setSearchParams({ lang: activeLang, level: activeLevel, unit: unit.id })}
                className="group cursor-pointer border-slate-200 transition-all hover:border-blue-300 hover:shadow-md dark:border-slate-800 dark:hover:border-blue-700"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl text-slate-900 dark:text-white">
                        {unit.title}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {unit.functions.join(' | ')}
                      </CardDescription>
                    </div>
                    <ChevronRight className="mt-1 h-5 w-5 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {unit.subUnits.map((sub) => (
                      <Badge key={sub.id} variant="secondary" className="text-xs">
                        {sub.title}
                      </Badge>
                    ))}
                  </div>
                  {unit.grammarPoints.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {unit.grammarPoints.map((gp) => (
                        <Badge key={gp.id} variant="outline" className="bg-purple-50 text-xs text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                          {gp.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LessonsPage;
